import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useBlockMedia } from './useBlockMedia';
import { MediaImage } from '../components/ui';
import { usePrefersReducedMotion } from '../hooks/useMediaQuery';

interface HeroData {
  headingLead?: string;
  headingAccentPrefix?: string;
  headingAccent?: string;
  body?: string;
  mediaId?: string;
  primaryCta?: { label?: string; href?: string };
  secondaryCta?: { label?: string; href?: string };
}

/**
 * Full-viewport hero, transcribed from the design: the photograph occupies the top 56%
 * with a downward scrim into solid black, and the copy sits bottom-left over the black.
 * The heading fades in shortly after mount unless reduced motion is requested.
 */
export function HeroBlock({ data }: { data: HeroData }) {
  const media = useBlockMedia(data.mediaId);
  const reduced = usePrefersReducedMotion();
  const [revealed, setRevealed] = useState(reduced);

  useEffect(() => {
    if (reduced) {
      setRevealed(true);
      return;
    }
    const timer = window.setTimeout(() => setRevealed(true), 80);
    return () => window.clearTimeout(timer);
  }, [reduced]);

  return (
    <section className="sz-hero" aria-labelledby="hero-heading">
      <MediaImage
        media={media}
        alt=""
        priority
        className="sz-hero__media"
        placeholder="Hero photograph"
        sizes="100vw"
      />
      <div className="sz-hero__scrim" aria-hidden="true" />
      <div className="sz-hero__base" aria-hidden="true" />

      <div className="sz-hero__content">
        <h1
          id="hero-heading"
          className="sz-hero__heading"
          style={{
            opacity: revealed ? 1 : 0,
            transition: reduced ? 'none' : 'opacity 1.1s ease',
          }}
        >
          {data.headingLead}
          <br />
          {data.headingAccentPrefix}
          <span style={{ color: 'var(--sz-gold)' }}>{data.headingAccent}</span>
        </h1>

        {data.body && <p className="sz-hero__body">{data.body}</p>}

        <div className="sz-hero__actions">
          {data.primaryCta?.href && (
            <Link to={data.primaryCta.href} className="sz-btn sz-btn--gold sz-btn--large">
              {data.primaryCta.label}
            </Link>
          )}
          {data.secondaryCta?.href && (
            <Link to={data.secondaryCta.href} className="sz-btn sz-btn--ghost sz-btn--large">
              {data.secondaryCta.label}
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
